// Package imports:
import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import { format } from 'date-fns';
import ISLocale from 'date-fns/locale/is';
// Component imports:
import DisplayBox from '../../DisplayBox/DisplayBox';
import Label from '../../../ui-elements/Label/Label';
import Alert from '../../../ui-elements/Alert/Alert';
import ChartLoading from '../../../ui-elements/ChartLoading/ChartLoading';
// Service imports:
import { getAPItoday, formatNumber } from '../../../services/utils';
import { useApiLmdData } from '../../../services/apiHooks';
import { SHORT_MONTHS } from '../../../services/config';
// Type imports:
import { IApiLmdIndexSnapshot } from '../../../types/HlutabrefTypes';
import { IDefaultProps } from '../../../types/Types';
import { IApiLmdCurrency } from '../../../types/CurrencyTypes';
import { IApiHistoryTimeseriesShortDATA } from '../../../types/MarketTypes';

type ChartType = 'index' | 'currency' | 'fund';

interface IChartDatum {
    x: number | undefined
    y: number | undefined
};

interface IChartDataInfo {
    name: string,
    data: IChartDatum[]
};


interface IOwnProps {
    chartType?: ChartType,
    symbol?: string,
    title?: string,
    height?: number,
};

type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const BasicChart: React.FC<Props> = ({
    chartType,
    symbol,
    title,
    height = 500,
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const baseCurr = symbol?.substring(0,3);
    const crossCurr = symbol?.substring(3,6);
    const today = new Date();
    const from = new Date(today.getFullYear() - 5, today.getMonth() + 1, today.getDate());
    const [apiData] = useApiLmdData<IApiLmdIndexSnapshot[] | IApiLmdCurrency[] | IApiHistoryTimeseriesShortDATA>(
        (chartType === 'index'
            ? `/v1/market_data/v1/indexes/symbol/${symbol}/snapshot?from_date=${getAPItoday(from)}&to_date=${getAPItoday(today)}`
            : chartType === 'currency'
            ? `/v1/market_data/v1/currency_cross/base_currency/${baseCurr?.toUpperCase()}/cross_currency/${crossCurr?.toUpperCase()}/history/?from_date=${getAPItoday(from)}&to_date=${getAPItoday(today)}`
            : `/v1/market_data/v1/funds/${symbol}/history_timeseries/?from_date=${getAPItoday(from)}&to_date=${getAPItoday(today)}`
        ),
        accessToken,
        refreshRateMs      
    );
    const [selectedRange, setSelectedRange] = useState(4);

    const divRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const { data, error } = apiData;
        if (data === null || error) return;

        const chartDataInfo: IChartDataInfo = {
            name: '',
            data: []
        }

        if (chartType === 'index') {
            chartDataInfo.name =  symbol ?? '';
            // If chart type is index, data is IApiLmdIndex[]
            // @ts-ignore
            for (let datum of data) {
                chartDataInfo.data.push({
                    x: datum.trade_date ? Date.parse(datum.trade_date.replace(/ /g, "T")) : undefined,
                    y: datum.current_value ?? undefined
                });
            }
        }
        if (chartType === 'currency') {
            // If chart type is currency, data is IApiLmdCurrency[]
            //@ts-ignore
            chartDataInfo.name = (data[0]?.Basecurrency ?? '') + (data[0]?.Crosscurrency ?? '');
            //@ts-ignore
            for (let datum of data) {
                chartDataInfo.data.push({
                    x: datum.Datetimerate ? Date.parse(datum.Datetimerate.replace(/ /g, "T")) : undefined,
                    y: datum.Averagerate ?? undefined
                });
            }
        }
        if (chartType === 'fund') {
            // If chart type is fund, data is IApiHistoryTimeseriesShortDATA
            //@ts-ignore
            chartDataInfo.name = data.name;
            //@ts-ignore
            for (let datum of data.data) {
                chartDataInfo.data.push({
                    x: datum[0] ?? undefined,
                    y: datum[1] ?? undefined
                });
            }
        }

        Highcharts.setOptions({
            lang: {
                shortMonths: SHORT_MONTHS,
                resetZoom: 'Til baka',
                resetZoomTitle: ''
            }
        });

        if (divRef.current !== null) {
            Highcharts.stockChart(divRef.current, {
                chart: {
                    height: height,
                    marginTop: 80,
                    reflow: true,
                    style: {
                        fontFamily: "Roboto"
                    },
                    zoomType: "xy",
                    selectionMarkerFill: '#4569EE18',
                    resetZoomButton: {
                        position: {
                            x: 0,
                            y: -75
                        },
                        theme: {
                            r: 6,
                            height: 28
                        }
                    },
                    plotBackgroundColor: {
                        linearGradient: {x1: 0, x2: 0, y1: 0, y2: 1},
                        stops: [
                            [0.05, '#FFF'],
                            [0.95, '#F8F8FA']
                        ]
                    }
                },
                exporting: {
                    buttons: {
                        contextButton: {
                            x: 30,
                            y: 36
                        }
                    }
                },
                credits: {
                    enabled: false
                },
                title: {
                    text: '',
                    align: 'center',
                    x: 60,
                    margin: -14
                },
                navigator: {
                    enabled: true,
                    adaptToUpdatedData: false,
                    handles: {
                        height: 22,
                        width: 6
                    },
                    maskFill: '#D4DEFF80',
                    series: {
                        lineWidth: 0,
                        opacity: 0
                    },
                    height: 20,
                    xAxis: {
                        labels: {
                            style: {
                                color: '#7B7C83',
                                fontSize: '10px'
                            },
                            y: -6
                        },
                        gridLineWidth: 0,
                        minTickInterval: 1000*60*60*24*365,
                        lineWidth: 0
                    },
                    yAxis: {
                        lineWidth: 0
                    },
                    outlineWidth: 0
                },
                scrollbar: {
                    enabled: false
                },
                tooltip: {
                    enabled: true,
                    formatter: function() {
                        //filtera einungis það sem er visible af því ef það er notað points[0] crashar allt þegar eitthvað series er disable-að
                        return (`<div class="chart-tooltip">
                                    <p class="tooltip-date">${format(this.x, 'EEEE, dd. MMM yyyy', {locale: ISLocale})}</p>
                                    ${this.points?.filter(x => x.point.visible === true).map(x => (
                                        `<p><span style="color: ${x.color}">${x.series.getName()}</span>: ${formatNumber(this.y, null, 3)}</p>`
                                    ))}
                                </div>`).replace(/>,</g,'><')
                    },
                    borderWidth: 0,
                    shadow: false,
                    useHTML: true,
                    shared: true
                },
                xAxis: [{
                    type: 'datetime',
                    title: {
                        text: null
                    },
                    minTickInterval: 1000*60*60*24*30,
                    tickWidth: 0
                }],
                yAxis: [{
                    title: {
                        text: chartType === 'index'
                            ? 'Gildi'
                            : chartType === 'currency'
                            ? 'Kross'
                            : 'Verð',
                        offset: 52,
                        x: 5
                    },
                    labels: {
                        x: -11,
                        y: 2
                    },
                    minTickInterval: 1,
                    gridLineDashStyle: 'Dash',
                    gridLineWidth: 1,
                    gridLineColor: '#D3D3D6',
                    opposite: false
                }],
                plotOptions: {
                    series: {
                        color: '#4569EE',
                        enableMouseTracking: true,
                        marker: {
                            enabled: false
                        },
                        animation: false,
                        turboThreshold: 4000
                    }
                },
                legend: {
                    enabled: true,
                    align: 'left',
                    verticalAlign: 'top',
                    itemStyle: {
                        color: '#232530',
                        fontSize: '14px',
                        fontWeight: '400',
                        lineHeight: '26px'
                    },
                    symbolWidth: 10,
                    x: 5,
                    y: 10
                },
                rangeSelector: {
                    allButtonsEnabled: true,
                    buttonPosition: {
                        align: 'right',
                        x: 0,
                        y: -80
                    },
                    buttons: [{
                        type: 'month',
                        count: 1,
                        text: '1 mán.',
                        title: '1 mánuður',
                        events: {
                            click: () => setSelectedRange(0)
                        }
                    }, {
                        type: 'month',
                        count: 3,
                        text: '3 mán.',
                        title: '3 mánuðir',
                        events: {
                            click: () => setSelectedRange(1)
                        }
                    }, {
                        type: 'month',
                        count: 6,
                        text: '6 mán.',
                        title: '6 mánuðir',
                        events: {
                            click: () => setSelectedRange(2)
                        }
                    }, {
                        type: 'ytd',
                        text: 'Þetta ár',
                        title: 'Þetta ár',
                        events: {
                            click: () => setSelectedRange(3)
                        }
                    }, {
                        type: 'year',
                        count: 1,
                        text: '1 ár',
                        title: '1 ár',
                        events: {
                            click: () => setSelectedRange(4)
                        }
                    }, {
                        type: 'year',
                        count: 3,
                        text: '3 ár',
                        title: '3 ár',
                        events: {
                            click: () => setSelectedRange(5)
                        }
                    }, {
                        type: 'year',
                        count: 5,
                        text: '5 ár',
                        title: '5 ár',
                        events: {
                            click: () => setSelectedRange(6)
                        }
                    }],
                    buttonSpacing: 5,
                    buttonTheme: {
                        r: 6,
                        height: 25,
                        width: 50
                    },
                    dropdown: 'never',
                    inputEnabled: false,
                    selected: selectedRange,
                    labelStyle: {
                        display: 'none'
                    }
                },
                responsive: {
                    rules: [{
                        condition: {
                            maxWidth: 369
                        },
                        chartOptions: {
                            rangeSelector: {
                                dropdown: 'always'
                            }
                        }
                    }, {
                        condition: {
                           callback: function() {
                               return window.matchMedia('(max-width: 767px)').matches;
                           }
                        },
                        chartOptions: {
                            chart: {
                                spacingLeft: 0,
                                spacingRight: 5,
                                marginTop: 40
                            },
                            legend: {
                                x: 10,
                                y: -20,
                                itemStyle: {
                                    fontSize: '12px'
                                }
                            },
                            navigator: {
                                margin: 13
                            },
                            rangeSelector: {
                                buttonPosition: {
                                    y: -40
                                },
                                buttonTheme: {
                                    width: 36,
                                    height: 24
                                }
                            },
                            xAxis: {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    }
                                }
                            },
                            yAxis: {
                                labels: {
                                    style: {
                                        fontSize: '9px'
                                    },
                                    x: -1
                                },
                                title: {
                                    offset: 27,
                                    x: 0,
                                    style: {
                                        fontSize: '10px'
                                    }
                                }
                            }
                        }
                    }]
                },
                series: [{
                    type: 'line',
                    id: 'AAPL',
                    name: chartDataInfo.name,
                    data: chartDataInfo.data,
                    tooltip: {
                        valueDecimals: 2
                    },
                    yAxis: 0
                }]
            }, () => {});
        }
    }, [apiData, divRef.current]);

    if(chartType === 'index'){
        if(apiData.data === null ){
            return <DisplayBox 
                        className='KCL_chart chart--basic'
                        title={title}
                        {...props}
                    >   
                        <div className='noDatainfo'>
                            <ChartLoading />
                        </div>
                    </DisplayBox>
        }
        //@ts-ignore
        if(apiData.data.length === 0){
            return <DisplayBox 
                        className='KCL_chart chart--basic'
                        title={title}
                        {...props}
                    >   
                        <div className='noDatainfo'>
                            <Alert type='info' headText={'Engin söguleg gögn fengust fyrir þessa vísitölu ' + symbol}/>
                        </div>
                    </DisplayBox>
        }
    }
    return (
        <DisplayBox 
            className='KCL_chart chart--basic'
            title={title}
            {...props}
        >
            <div className="chart" ref={divRef}></div>
            {( chartType === 'currency') 
            ? <a className="label-wrap" href="https://sedlabanki.is" target="_blank" rel="noopener noreferrer">
                <Label text="Seðlabanki Íslands" labelType="origin" />
            </a>
            : null
            }
        </DisplayBox>
    );
}

export default BasicChart;